import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
// import { AdalService } from 'ng2-adal/dist/core';
import { FlowManagementService } from '../services/flow-management.service';
//import { MsalService } from '@azure/msal-angular';
import { StorageRouterService } from '../services/storage-router.service';
import { Settings } from '../flows/settings';
import { MsalHelperService } from '../services/msal-helper.service';
import { MsalBroadcastService, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { InteractionStatus } from '@azure/msal-browser';

@Component({
    templateUrl: './oauth-callback.component.html',
    standalone: false
})
export class OAuthCallbackComponent extends MsalRedirectComponent implements OnInit, OnDestroy {


  public lastRoute = '';
  public reason: any = null;
  public error: any = null;
  subscription = new Subscription();

  constructor(
    private router: Router,
    private msalHelperService: MsalHelperService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
//    private flowManagementService: FlowManagementService,
    private storageDexieService: StorageRouterService
  ) {
    super(msalService)
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    this.log('init', this.getRedirectDiagnostics());

    // Always let MSAL process redirect responses (auth code/hash) first.
    this.log('handleRedirect.start');
    super.ngOnInit();

    // Silent token renew uses a hidden iframe. Keep this callback page minimal
    // and avoid app navigation in iframe context to prevent timeouts.
    if (window.parent !== window && !window.opener) {
      this.log('iframe.detected.skipNavigation');
      return;
    }

    this.subscription.add(
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          take(1)
        )
        .subscribe(() => {
          this.log('interaction.none');
          this.finishRedirect().catch((reason) => {
            this.reason = this.sanitizeReason(reason);
            this.log('finishRedirect.error', this.reason);
          });
        })
    );
  }

  private async finishRedirect(): Promise<void> {
    this.log('finishRedirect.start', this.getRedirectDiagnostics());
    this.error = this.msalHelperService.getError();
    if (this.error) {
      this.log('finishRedirect.hashError', this.error);
      return;
    }

    const user = this.msalHelperService.getUser();
    if (!user) {
      this.reason = { message: 'No active account was found after redirect handling completed.' };
      this.log('finishRedirect.noActiveAccount', {
        accountCount: this.msalService.instance.getAllAccounts().length
      });
      return;
    }

    this.log('finishRedirect.accountFound', {
      username: user.username,
      homeAccountId: user.homeAccountId,
      accountCount: this.msalService.instance.getAllAccounts().length
    });

    await this.msalHelperService.trySilentM365Tokens();
    this.log('finishRedirect.silentWarmup.done');

    this.getLastRoute().then(route => {
      this.log('finishRedirect.lastRoute', { route });
      this.lastRoute = route;
      this.router.navigateByUrl(route).catch((reason) => {
        this.reason = this.sanitizeReason(reason);
        this.log('finishRedirect.navigate.error', this.reason);
      }).then((success) => {
        if (success) {
          this.log('finishRedirect.navigate.success', { route });
        }
      });
    });
  }

  async getLastRoute() {
    try {
      var setting = await this.storageDexieService.getConfig(Settings.SETTING_LASTROUTE);
      if (this.isRestorableRoute(setting?.value)) {
        this.log('lastRoute.storage', { route: setting.value });
        return setting.value;
      }
    } catch {
      this.log('lastRoute.storage.error');
    }

    const fallback = window.sessionStorage.getItem('flowstudio.lastRoute') || window.localStorage.getItem('flowstudio.lastRoute');
    const route = this.isRestorableRoute(fallback) ? fallback : "/flows";
    this.log('lastRoute.fallback', { fallback, route });
    return route;
  }

  private isRestorableRoute(route: string): boolean {
    return !!route && route !== '/' && !route.startsWith('/settings');
  }

  private getRedirectDiagnostics(): Record<string, unknown> {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return {
      path: window.location.pathname,
      hasHash: hash.length > 0,
      hashKeys: this.getUrlPartKeys(hash),
      hasSearch: search.length > 0,
      searchKeys: this.getUrlPartKeys(search),
      accountCount: this.msalService.instance.getAllAccounts().length,
      hasActiveAccount: !!this.msalService.instance.getActiveAccount()
    };
  }

  private getUrlPartKeys(value: string): string[] {
    const trimmed = value.replace(/^[#?]/, '');
    if (!trimmed) {
      return [];
    }

    return trimmed
      .split('&')
      .map((part) => part.split('=')[0])
      .filter((key) => !!key);
  }

  private sanitizeReason(reason: any): any {
    if (!reason) {
      return reason;
    }

    return {
      name: reason.name,
      message: reason.message || `${reason}`,
      errorCode: reason.errorCode,
      subError: reason.subError,
      status: reason.status
    };
  }

  private log(event: string, details?: any): void {
    if (details === undefined) {
      console.log(`[OAuthCallback] ${event}`);
      return;
    }

    console.log(`[OAuthCallback] ${event}`, details);
  }

}
